import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { LaTeXPreviewContent } from '../../LaTeXPreviewContent';
import 'katex/dist/katex.min.css';

/**
 * TruncatedText component for expandable text display with LaTeX support
 * Shows a truncated version of text with a "Show more" button when text exceeds maxLength
 * Renders LaTeX expressions enclosed in dollar signs using react-katex
 *
 * @param {Object} props - Component properties
 * @param {string} props.text - The text to display (can include LaTeX enclosed in $ symbols)
 * @param {number} props.maxLength - Maximum length before truncation (default: 70 characters)
 * @param {string} props.className - Additional CSS classes for the container
 */
const TruncatedText = ({ text, maxLength = 70, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Process the text to remove VISUAL tags for length calculation
  // Safe to call even if text is null/undefined as we handle that case
  const processedText = useMemo(() => {
    if (!text) {
      return '';
    }
    // Replace <VISUAL>...</VISUAL> with "visual description" for length calculations
    return text.replace(/<VISUAL>([\s\S]*?)<\/VISUAL>/g, '[visual description]');
  }, [text]);

  if (!text) {
    return null;
  }

  const needsTruncation = processedText.length > maxLength;

  // For display purposes, if we need truncation and not expanded:
  // 1. If there are VISUAL tags, we want to keep the opening text and then add ...
  // 2. If no VISUAL tags, just truncate normally
  const displayText =
    isExpanded || !needsTruncation ? text : processedText.substring(0, maxLength) + '...';

  return (
    <div className={`${className}`}>
      <div className="whitespace-pre-wrap">
        <LaTeXPreviewContent content={displayText} />
      </div>
      {needsTruncation && (
        <button
          className="mt-1 text-[#e4b17a] hover:text-[#c99b69] text-sm font-medium flex items-center transition-all duration-200"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <>
              <span>Show less</span>
              <ChevronUpIcon className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              <span>Show more</span>
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default TruncatedText;
